import { type IconDefinition } from '@/components/ui/app-icon';
import {
  iconBuilding,
  iconMobileScreenButton,
  iconPhone,
  iconPhoneVolume,
} from '@/lib/icons';

/** Canonical phone presentation. Keep raw evidence available separately and
 * never infer mobile from a missing or ambiguous provider type. */

export const UNKNOWN_PHONE_TYPE = 'unknown';

/** Canonical types with specific icons. */
const PHONE_TYPE_ICONS: Record<string, IconDefinition> = {
  mobile: iconMobileScreenButton,
  work: iconBuilding,
  office: iconBuilding,
  landline: iconPhoneVolume,
  voip: iconPhoneVolume,
  direct: iconPhoneVolume,
  toll_free: iconPhone,
};

export interface ResolvedPhoneType {
  /** The provider's own value, trimmed — or `unknown` when genuinely absent. */
  raw: string;
  canonical: string;
  /** Human-readable canonical label. */
  label: string;
  icon: IconDefinition;
  /** True only when a provider EXPLICITLY said mobile. Mirrors the backend's
   * `is_explicit_mobile`; nothing else may be treated as a mobile. */
  isExplicitMobile: boolean;
}

const titleCase = (value: string) =>
  value
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/**
 * Look up `number` in `metadata` and describe its type for display.
 *
 * Tolerates every shape the API can produce — absent metadata, a non-object
 * entry, a non-string `type`, an empty string — by resolving to `unknown`
 * rather than throwing or guessing.
 */
export const resolvePhoneType = (
  number: string | null | undefined,
  metadata?: Record<string, unknown> | null
): ResolvedPhoneType => {
  let raw = UNKNOWN_PHONE_TYPE;

  if (number && metadata && typeof metadata === 'object') {
    const entry = (metadata as Record<string, unknown>)[number.trim()];
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      const value = (entry as Record<string, unknown>).type;
      if (typeof value === 'string' && value.trim()) {
        raw = value.trim();
      }
    }
  }

  const key = canonicalPhoneType(raw);
  return {
    raw,
    canonical: key,
    label: titleCase(key),
    icon: PHONE_TYPE_ICONS[key] ?? iconPhone,
    isExplicitMobile: key === 'mobile',
  };
};

/** The provider's own status for a number (e.g. Wiza's `phone_status`), if any. */
export const resolvePhoneStatus = (
  number: string | null | undefined,
  metadata?: Record<string, unknown> | null
): string | undefined => {
  if (!number || !metadata || typeof metadata !== 'object') return undefined;
  const entry = (metadata as Record<string, unknown>)[number.trim()];
  if (!entry || typeof entry !== 'object' || Array.isArray(entry))
    return undefined;
  const value = (entry as Record<string, unknown>).status;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

const DIRECT_PHONE_TYPES = new Set([
  'direct',
  'direct_line',
  'direct_dial',
  'landline',
  'voip',
  'fixedvoip',
  'nonfixedvoip',
]);

/** Consecutive phone fields: mobile, direct, then other. Keep every number
 * and the provider's relative order within each priority. Never mutate input. */
export const prioritizePhoneNumbers = (
  numbers?: readonly unknown[] | null,
  metadata?: Record<string, unknown> | null
): string[] => {
  const buckets: string[][] = [[], [], []];
  const seen = new Set<string>();
  for (const value of numbers ?? []) {
    if (typeof value !== 'string' || !value.trim()) continue;
    const number = value.trim();
    if (seen.has(number)) continue;
    seen.add(number);
    const type = resolvePhoneType(number, metadata)
      .raw.toLowerCase()
      .replace(/[\s-]+/g, '_');
    const priority =
      type === 'mobile' ? 0 : DIRECT_PHONE_TYPES.has(type) ? 1 : 2;
    buckets[priority].push(number);
  }
  return buckets.flat();
};

export function canonicalPhoneType(raw: string): string {
  const key = raw
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const aliases: Record<string, string> = {
    direct_line: 'direct',
    direct_dial: 'direct',
    fixed: 'landline',
    fixed_line: 'landline',
    fixedvoip: 'voip',
    nonfixedvoip: 'voip',
    work: 'office',
    business: 'office',
    company: 'office',
    hq: 'office',
    main: 'office',
    switchboard: 'office',
    uan: 'office',
    tollfree: 'toll_free',
    fixed_line_or_mobile: 'unknown',
    none: 'unknown',
    null: 'unknown',
    '': 'unknown',
  };
  if (aliases[key]) return aliases[key];
  return [
    'mobile',
    'direct',
    'landline',
    'voip',
    'office',
    'toll_free',
    'unknown',
    'other',
  ].includes(key)
    ? key
    : 'other';
}
